import { useEffect, useRef, useCallback } from 'react';
import { useUIModelApi } from '../contexts/UIModelApiContext';

interface AutoSaveConfig {
  debounceMs?: number;
  enabled?: boolean;
  pageContainerRows?: {[pageId: string]: Record<string, number>}; 
  onSave?: (section: string) => void;
  onError?: (section: string, error: any) => void;
}

export const useAutoSave = ({ 
  debounceMs = 2000,
  enabled = true,
  pageContainerRows,
  onSave,
  onError
}: AutoSaveConfig = {}) => {
  const {
    saveHeader,
    saveFooter,
    saveNavigation,
    saveCanvas,
    currentUIModelId,
    isLoading
  } = useUIModelApi();
  
  const changedSections = useRef<Set<string>>(new Set());
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  const performAutoSave = useCallback(async () => {
    
    const sectionsToSave = Array.from(changedSections.current);
    changedSections.current.clear();
    
    for (const section of sectionsToSave) {
        try {
            switch (section) {
                case 'header':
                    await new Promise(resolve => setTimeout(resolve, 0));
                    await saveHeader();
                    onSave?.(section);
                    break;
                case 'footer':
                    await new Promise(resolve => setTimeout(resolve, 0));
                    await saveFooter();
                    onSave?.(section);
                    break;
                case 'navigation':
                    await saveNavigation();
                    onSave?.(section);
                    break;
                case 'canvas':
                    await saveCanvas(pageContainerRows);
                    onSave?.(section);
                    break;
            }
        } catch (error) {
            console.error(`Failed to save ${section}:`, error);
            onError?.(section, error);
            changedSections.current.add(section);
        }
    }
}, [saveHeader, saveFooter, saveNavigation, saveCanvas, onSave, onError, isLoading]);

  const markChanged = useCallback((section: 'header' | 'footer' | 'navigation' | 'canvas') => {
    
    if (!enabled || !currentUIModelId || isLoading) {
      console.log('markChanged early return:', { enabled, currentUIModelId, isLoading });
      return;
    }
    
    changedSections.current.add(section);
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      performAutoSave();
    }, debounceMs);
  }, [enabled, currentUIModelId, debounceMs, isLoading, performAutoSave]); // Add performAutoSave to deps

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        if (changedSections?.current?.size > 0) {
          performAutoSave();
        }
      }
    };
  }, [performAutoSave]);

  return {
    markChanged,
    performAutoSave,
    hasPendingChanges: () => changedSections.current.size > 0
  };
};